/**
 * Rental system — where this Company's jobs draft into.
 *
 * The one section that changes what the product does rather than what it
 * says: intake takes a call and produces the demand, and the rental
 * system named here turns that into a priced job. Named nothing, an
 * inquiry rests failed rather than drafting somewhere nobody chose.
 *
 * Every system is connected the same way: what names the account and a
 * key that system issued, proved before it is stored so a typo fails in front of
 * the person who made it. A Company's own Current RMS was authorized
 * instead until ADR-0019 was superseded — the authorize step refuses an
 * application registered in another account, so the one Custom
 * Application that design rested on could never have served a second
 * partner, and what is left is the key its System Setup issues.
 *
 * Nothing ever reads a stored credential back, either way.
 */
import { useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { Select } from "../../components/ui/Select";
import {
  connectRentalSystem,
  disconnectRentalSystem,
  getRentalSystem,
  listCurrentRMSStores,
  type CurrentRMSStore,
  type RentalSystem,
  type RentalSystemKind,
} from "../../lib/settings-api";
import { RENTAL_SYSTEM_NAMES, readRentalSystem } from "../../lib/connections";
import { useAction } from "../../lib/use-action";
import { useSettingsSource } from "../../lib/use-settings-source";
import { FieldLabel, Loading, SectionHeader } from "./section-chrome";
import { JobNameRow } from "./JobNameRow";
import styles from "./RentalSystemSection.module.css";

const KIND_OPTIONS = Object.entries(RENTAL_SYSTEM_NAMES).map(
  ([value, label]) => ({ value, label }),
);

export function RentalSystemSection() {
  const source = useSettingsSource(getRentalSystem);
  const { loadError } = source;
  const system = source.data?.rental_system ?? null;
  const slug = source.data?.slug ?? "";
  const setSystem = (next: RentalSystem) =>
    source.setData((d) => d && { ...d, rental_system: next });
  // Set while replacing a working connection: the form is shown over a
  // connection that still stands, and Cancel puts it back.
  const [replacing, setReplacing] = useState(false);

  const connected = system !== null && readRentalSystem(system).state !== "none";

  return (
    <section>
      <SectionHeader
        title="Rental system"
        description="Where your enquiries are drafted as priced jobs."
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      {!system ? (
        <Loading />
      ) : connected && !replacing ? (
        <>
          <ConnectedState
            system={system}
            onReplace={() => setReplacing(true)}
            onDisconnected={(next) => {
              setSystem(next);
              setReplacing(false);
            }}
          />
          {/* Only with a connection: a name is for a job drafted somewhere. */}
          <JobNameRow systemName={readRentalSystem(system).name} />
        </>
      ) : (
        <>
          {/* Not connected is a state and not a gap: calls are written up
              and emailed, and connecting is what turns drafting on. */}
          {!connected && (
            <p className={styles.notConnected}>
              Not connected. Calls and enquiries are written up and emailed
              to you. Connect your rental system to have them drafted into
              it as well.
            </p>
          )}
          <ConnectForm
            slug={slug}
            onCancel={connected ? () => setReplacing(false) : undefined}
            onConnected={(next) => {
              setSystem(next);
              setReplacing(false);
            }}
          />
        </>
      )}
    </section>
  );
}

/** What a working (or broken) connection reads as, and the two ways out. */
function ConnectedState({
  system,
  onReplace,
  onDisconnected,
}: {
  system: RentalSystem;
  onReplace: () => void;
  onDisconnected: (next: RentalSystem) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const {
    working,
    error,
    setError,
    run,
  } = useAction();

  const disconnect = async () => {
    await run(async () => {
      onDisconnected(await disconnectRentalSystem());
      setConfirming(false);
    });
  };

  return (
    <div>
      {system.status === "broken" && (
        <Banner variant="warning" className={styles.notice}>
          {system.last_error ||
            "The last call to this rental system failed. Paste a new key to fix it."}
        </Banner>
      )}

      <dl className={styles.facts}>
        <Fact label="System" value={readRentalSystem(system).name} />
        <Fact label="Account" value={system.address ?? "Not set"} />
        {system.store_id != null && (
          <Fact label="Store" value={String(system.store_id)} />
        )}
      </dl>

      <div className={styles.actions}>
        <Button size="sm" variant="secondary" onClick={onReplace}>
          Replace key
        </Button>
        <Button size="sm" variant="danger" onClick={() => setConfirming(true)}>
          Disconnect
        </Button>
      </div>

      <ConfirmModal
        open={confirming}
        title="Disconnect this rental system?"
        message="New enquiries stop being drafted until you connect another one."
        confirmLabel="Disconnect"
        loading={working}
        error={error}
        onConfirm={() => void disconnect()}
        onCancel={() => {
          setConfirming(false);
          setError(null);
        }}
      />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}

/**
 * The connect form. A submit button, not a commit-on-blur row: this is
 * one decision made from several fields at once, and half of it saved is
 * a Company drafting nowhere.
 *
 * Current RMS takes one more press than HireOS, and it has to. An
 * account with several warehouses must name the one its opportunities
 * come out of — picking for them would put every job in the wrong
 * warehouse, silently — and the question cannot be asked until there is
 * a credential to ask it with. So the first press proves the key and
 * reads the account's warehouses, saving nothing; an account with one
 * warehouse never sees the question at all.
 */
function ConnectForm({
  slug,
  onCancel,
  onConnected,
}: {
  slug: string;
  onCancel?: () => void;
  onConnected: (next: RentalSystem) => void;
}) {
  const [kind, setKind] = useState<RentalSystemKind>("hireos");
  const [baseURL, setBaseURL] = useState("https://api.hireos.co.nz");
  const [subdomain, setSubdomain] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [stores, setStores] = useState<CurrentRMSStore[] | null>(null);
  const [storeID, setStoreID] = useState(0);
  const {
    working: busy,
    error,
    setError,
    run,
  } = useAction();

  // A warehouse list belongs to the credential that produced it, so
  // editing either half of that credential drops it rather than
  // connecting the new key to the old account's store.
  const forgetStores = () => {
    setStores(null);
    setStoreID(0);
  };

  // Changing the system throws away what was typed for the other one:
  // the two share the key field and nothing else, and carrying a
  // workspace address into a Current RMS form would be carrying nonsense.
  const changeKind = (next: string) => {
    setKind(next as RentalSystemKind);
    setError(null);
    forgetStores();
  };

  const save = async () => {
    await run(async () => {
      if (kind === "hireos") {
        onConnected(
          await connectRentalSystem({
            kind,
            base_url: baseURL,
            api_key: apiKey,
          }),
        );
        return;
      }
      if (kind === "rentman") {
        // The account is optional: it only links a drafted request back
        // into their screen, and the token is proved on the server.
        onConnected(
          await connectRentalSystem({
            kind,
            api_key: apiKey.trim(),
            subdomain: subdomain.trim(),
          }),
        );
        return;
      }
      let chosen = storeID;
      if (stores === null) {
        const found = await listCurrentRMSStores({
          subdomain: subdomain.trim(),
          api_key: apiKey.trim(),
        });
        setStores(found);
        setStoreID(found[0]?.id ?? 0);
        if (found.length !== 1) return;
        chosen = found[0].id;
      }
      onConnected(
        await connectRentalSystem({
          kind,
          api_key: apiKey.trim(),
          subdomain: subdomain.trim(),
          store_id: chosen,
        }),
      );
    });
  };

  const canSave =
    kind === "hireos"
      ? baseURL.trim() !== "" && apiKey.trim() !== ""
      : kind === "rentman"
        ? apiKey.trim() !== ""
        : subdomain.trim() !== "" && apiKey.trim() !== "";

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div>
        <FieldLabel htmlFor="rental-system-kind">Rental system</FieldLabel>
        <Select
          id="rental-system-kind"
          value={kind}
          options={KIND_OPTIONS}
          onChange={changeKind}
          className={styles.select}
        />
      </div>

      {kind === "hireos" ? (
        <>
          <div>
            <FieldLabel
              htmlFor="rental-system-base-url"
              tooltip="The API host of the HireOS workspace your jobs live in."
            >
              Workspace address
            </FieldLabel>
            <input
              id="rental-system-base-url"
              type="url"
              className={styles.input}
              value={baseURL}
              onChange={(e) => setBaseURL(e.target.value)}
            />
          </div>

          <div>
            <FieldLabel htmlFor="rental-system-key">API key</FieldLabel>
            <p className={styles.fieldNote}>
              Minted in the workspace under Settings → Integrations, for{" "}
              {slug || "your company"}.
            </p>
            <input
              id="rental-system-key"
              type="password"
              autoComplete="off"
              className={styles.input}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
        </>
      ) : kind === "rentman" ? (
        <>
          <div>
            <FieldLabel htmlFor="rental-system-subdomain">
              Rentman account{" "}
              <span className={styles.optional}>(optional)</span>
            </FieldLabel>
            <p className={styles.fieldNote}>
              Links each drafted request back to your Rentman screen.
            </p>
            <div className={styles.suffixField}>
              <input
                id="rental-system-subdomain"
                type="text"
                className={styles.suffixInput}
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
              />
              <span className={styles.suffix}>.rentmanapp.com</span>
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="rental-system-key">API token</FieldLabel>
            <p className={styles.fieldNote}>
              Made in Rentman under Configuration → Integrations → API.
              Generating a new one there stops the old one working.
            </p>
            <input
              id="rental-system-key"
              type="password"
              autoComplete="off"
              className={styles.input}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
        </>
      ) : (
        <>
          <div>
            <FieldLabel htmlFor="rental-system-subdomain">
              Current RMS subdomain
            </FieldLabel>
            {/* The suffix is shown rather than described. A sentence
                saying "the part before .current-rms.com" is a rule the
                reader has to apply to their own address bar; the domain
                sitting at the end of the box is the same fact in the
                place the answer goes, and it says what to type by
                leaving exactly the gap the answer fills.

                The wrapper carries the field's border and its focus
                state, so the pair reads as one input: the shared field
                look, minus the border and width the wrapper now owns. */}
            <div className={styles.suffixField}>
              <input
                id="rental-system-subdomain"
                type="text"
                className={styles.suffixInput}
                value={subdomain}
                onChange={(e) => {
                  setSubdomain(e.target.value);
                  forgetStores();
                }}
              />
              <span className={styles.suffix}>
                .current-rms.com
              </span>
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="rental-system-key">API key</FieldLabel>
            <p className={styles.fieldNote}>
              Made in Current RMS under System Setup → Integrations → API keys.
            </p>
            <input
              id="rental-system-key"
              type="password"
              autoComplete="off"
              className={styles.input}
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                forgetStores();
              }}
            />
          </div>

          {stores && stores.length > 1 && (
            <div>
              <FieldLabel htmlFor="rental-system-store">Store</FieldLabel>
              <p className={styles.fieldNote}>
                Which warehouse your drafted opportunities come out of.
              </p>
              <Select
                id="rental-system-store"
                value={String(storeID)}
                options={stores.map((store) => ({
                  value: String(store.id),
                  label: store.address
                    ? store.name + ", " + store.address
                    : store.name,
                }))}
                onChange={(next) => setStoreID(Number(next))}
                className={styles.select}
              />
            </div>
          )}
        </>
      )}

      {error && <InlineBanner variant="error">{error}</InlineBanner>}

      <div className={styles.formActions}>
        <Button size="sm" type="submit" loading={busy} disabled={!canSave}>
          Connect
        </Button>
        {onCancel && (
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
